import SubmitButton from "Components/Buttons/SubmitButton";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { addTag, fetchTagById, updateTag } from "slices/tag/thunk";
import * as Yup from "yup";

const TagForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const location = useLocation();
  const [initialData, setInitialData] = useState({
    tagName: "",
  });
  const dispatch = useDispatch<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const tagDataFromState = (location.state as any)?.tag;

  // Fetch existing tag if editing
  useEffect(() => {
    if (isEdit) {
      if (tagDataFromState) {
        setInitialData({ tagName: tagDataFromState.tag_name });
      } else if (id) {
        setIsLoading(true);
        dispatch(fetchTagById(id))
          .then((res: any) => {
            setInitialData({ tagName: res?.payload?.tag_name || "" });
          })
          .catch(() => toast.error("Failed to load tag details"))
          .finally(() => setIsLoading(false));
      }
    }
  }, [isEdit, id, tagDataFromState, dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialData,
    validationSchema: Yup.object({
      tagName: Yup.string().required("Please enter tag name"),
    }),
    onSubmit: async (values, { setFieldError }) => {
      const payload = { tag_name: values.tagName.trim() };
      const existingTags: { tag_name: string }[] = [];
      const isDuplicate =
        !isEdit &&
        existingTags.some(
          (t) =>
            t.tag_name.toLowerCase() === values.tagName.trim().toLowerCase()
        );

      if (isDuplicate) {
        setFieldError("tagName", "Tag with this name already exists");
        toast.error("Tag with this name already exists");
        return;
      }

      try {
        setIsLoading(true);

        if (isEdit) {
          await dispatch(
            updateTag({ id: tagDataFromState?.tag_id || id, ...payload })
          ).unwrap();
          toast.success("Tag updated successfully");
          navigate(-1);
        } else {
          await dispatch(addTag(payload)).unwrap();
          toast.success("Tag added successfully");
          navigate(-1);
        }
      } catch (error: any) {
        console.error("Tag form error:", error);

        if (error?.errors?.tag_name?.[0]) {
          const msg = error.errors.tag_name[0];
          setFieldError("tagName", msg);
          toast.error(msg);
        } else {
          toast.error(error?.message || "Failed to save tag");
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className="page-content px-4">
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "30px",
          boxShadow: "0 0 15px rgba(0,0,0,0.05)",
        }}
      >
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
          {/* Left: Back icon and heading */}
          <div className="d-flex align-items-center gap-2">
            <span
              style={{ fontSize: "24px", cursor: "pointer" }}
              onClick={() => navigate(-1)}
            >
              ←
            </span>
            <h5 className="mb-0 fw-bold">{isEdit ? "Edit Tag" : "Add Tag"}</h5>
          </div>

           {/* Right: Breadcrumb */}
           <div className="text-muted">
            <span
              className="text-muted"
              style={{ fontSize: "0.9rem", cursor: "pointer" }}
              onClick={() => navigate("/tag")}
            >
              Tags
            </span>{" "}
            &gt;{" "}
            <span
              className="text-muted"
              style={{ fontSize: "0.9rem", cursor: "pointer" }}
            >
              {id ? "Edit Tag" : "Add Tag"}
            </span>{" "}
          </div>
        </div>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Form autoComplete="off" onSubmit={formik.handleSubmit}>
            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <Form.Label>
                    Tag Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="tagName"
                    placeholder="Enter Tag Name"
                    value={formik.values.tagName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.tagName && formik.touched.tagName
                    }
                    style={{
                      height: "40px",
                      lineHeight: "1.5",
                      fontSize: "0.875rem",
                    }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.tagName}
                  </Form.Control.Feedback>
                </div>
              </Col>
            </Row>

            <div className="d-flex justify-content-center gap-3 py-3">
              <Button
                variant="outline-secondary"
                style={{
                  color: "#878899",
                  borderColor: "#B0BAC9",
                  backgroundColor: "transparent",
                }}
                onClick={() => navigate(-1)}
                disabled={isLoading}
              >
                Cancel
              </Button>

              {/* <Button
                type="submit"
                className="btn"
                style={{
                  backgroundColor: "#0073BB",
                  borderColor: "#0073BB",
                  color: "#fff",
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Spinner size="sm" animation="border" />
                ) : isEdit ? (
                  "Update"
                ) : (
                  "Add"
                )}
              </Button> */}

              {/* Reusable submit button with spinner */}
              <SubmitButton
                isSubmitting={formik.isSubmitting}
                isEdit={isEdit}
              />
            </div>
          </Form>
        )}
      </div>
    </div>
  );
};

export default TagForm;
